import React, { useState } from 'react';
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  Link,
  Checkbox,
  Alert,
  AlertIcon,
  useToast,
  Spinner
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { getConfigValue } from '../../config/runtimeConfig';

const Signup = () => {
  const { loginWithRedirect, isLoading } = useAuth0();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    agreeTerms: false
  });
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useToast();
  
  // Domain configuration (case-insensitive)
  const domainFromEnv = getConfigValue(
    'REACT_APP_ALLOWED_EMAIL_DOMAIN',
    process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN,
    'teliviit.com'
  );
  const ALLOWED_DOMAIN = domainFromEnv.toLowerCase();
  const DOMAIN_DISPLAY = getConfigValue(
    'REACT_APP_ALLOWED_EMAIL_DOMAIN_DISPLAY',
    process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN_DISPLAY,
    domainFromEnv
  );
  const DOMAIN_ERROR = `Only @${DOMAIN_DISPLAY} emails allowed`;

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'agreeTerms' ? checked : value
    }));
  };

  const validateEmail = (email) => {
    if (!email) return false;
    
    // Basic email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return false;
    }
    
    // Domain check (case-insensitive)
    const domain = email.substring(email.lastIndexOf('@') + 1).toLowerCase();
    return domain === ALLOWED_DOMAIN;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);
    
    const trimmedEmail = formData.email.trim();
    
    if (!validateEmail(trimmedEmail)) {
      setError(DOMAIN_ERROR);
      return;
    }

    if (!formData.agreeTerms) {
      setError('You must accept the terms and conditions');
      return;
    }

    try {
      setIsProcessing(true);
      await loginWithRedirect({
        screen_hint: 'signup',
        authorizationParams: {
          connection: 'Username-Password-Authentication',
          login_hint: trimmedEmail,
          domain_hint: ALLOWED_DOMAIN
        },
        appState: {
          returnTo: '/verify-email'
        }
      });
      
      toast({
        title: 'Verification Email Sent',
        description: `We've sent a confirmation link to ${trimmedEmail}. Please check your inbox.`,
        status: 'success',
        duration: 8000,
        isClosable: true,
        position: 'top'
      });
    } catch (err) {
      toast({
        title: 'Signup Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Styling
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg={bgColor}>
      <Box
        w="full"
        maxW="md"
        p={8}
        borderRadius="lg"
        boxShadow="xl"
        bg={cardBg}
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Heading as="h1" size="xl" mb={2}>
              Sign Up
            </Heading>
            <Text color="gray.500">
              Create your @{DOMAIN_DISPLAY} account
            </Text>
          </Box>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={handleSignup}>
            <VStack spacing={4}>
              <FormControl id="email" isRequired>
                <FormLabel>Work Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={`you@${DOMAIN_DISPLAY}`}
                  autoComplete="email"
                />
              </FormControl>

              <FormControl id="password" isRequired>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  <InputRightElement h="full">
                    <Button
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Checkbox
                name="agreeTerms"
                isChecked={formData.agreeTerms}
                onChange={handleChange}
                colorScheme="blue"
              >
                I agree to the{' '}
                <Link color="blue.500" href="/terms" isExternal>
                  Terms and Conditions
                </Link>
              </Checkbox>

              <Button
                type="submit"
                colorScheme="blue"
                width="full"
                isLoading={isProcessing}
                loadingText="Creating account..."
              >
                Submit
              </Button>
            </VStack>
          </form>

          <Text textAlign="center" fontSize="sm" color="gray.500">
            Already have an account?{' '}
            <Link color="blue.500" href="/login">
              Log in
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

export default Signup;
